// client/src/navbar/Navbar.jsx
import React, { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation} from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { navConfig } from './data/navbarConfig';

import "./index.css";

const Navbar = () => {
  const theme = useTheme();  // Access the current Material-UI theme object (e.g., breakpoints, palette)
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));  // Check if the current screen width is below the 'md' breakpoint (typically <960px)
  const location = useLocation();  // Get the current location object from React Router (e.g., pathname, search, hash)

  const [menuState, setMenuState] = useState({
    desktopMenuAnchor: null,
    desktopSubmenuOpen: null,

    mobileMenuAnchor: null,
    mobileSubmenuOpen: null,
  });
  
  const updateMenuState = (key, value) => {
    setMenuState((prev) => ({ ...prev, [key]: value }));
  };

  const handleMenuClose = () => {
    updateMenuState('desktopMenuAnchor', null);
    updateMenuState('mobileMenuAnchor', null);
    updateMenuState('desktopSubmenuOpen', null);  // Close desktop submenu too
    updateMenuState('mobileSubmenuOpen', null);  // Close mobile submenu too
  };

  const isLinkActive = (link) => location.pathname === link;  // Check if the current route matches the given link (used to highlight active nav links)

  return (
    <div className='nav-container'>
      <AppBar position="fixed" sx={{ top: 0, bgcolor: '#6B4226', wShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)' }}>
        <Toolbar sx={{      
          justifyContent: 'center',
          position: 'relative',
          alignItems: 'center',
          flexWrap: 'wrap',          
          minHeight: 64,
        }}>
        {/* ---------------------------------------------LOGO START--------------------------------------- */}
          {/* Logo Section */}
          <Typography
            variant="h6"
            component="div"
            noWrap
            sx={{
              position: 'absolute',
              left: '47%',              
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            <Button
              color="inherit"
              component={Link}
              to="/"
              sx={{
                fontFamily: '"Raleway", sans-serif',
                fontSize: {
                  xs: '1.1rem',
                  sm: '1.2rem',
                  md: '1.15rem',  // medium screens (tablets, small laptops)
                  lg: '1.4rem',   // large screens (desktops)
                  xl: '1.5rem',  // extra-large screens (large desktops, TVs)
                },
                color: '#FAEBD7', // creamy latte (better contrast on #6B4226)
                padding: 0,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                '&:hover': {
                  color: '#DAA520', // Golden amber; classic café warmth and brightness.
                },
              }}
            >

              <img
                src="https://res.cloudinary.com/ddtded6wc/image/upload/v1761133474/coffee-cup_u5pdxu.png"
                alt="Coffee Cup Logo"
                style={{
                  width: '28px',
                  height: '28px',
                  marginRight: '8px',
                  verticalAlign: 'middle',
                }}
              />
              Better Home Coffee
            </Button>
          </Typography>

        {/* ---------------------------------------------MOBILE MENU START-------------------------------------------- */}
        {isMediumScreen && (
          <>
            {/* Mobile Menu Hamburger Icon */}
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ display: { xs: 'block', md: 'none' }, marginLeft: 'auto', cursor: 'pointer', marginTop: '-8px', }}
              onClick={(e) => updateMenuState('mobileMenuAnchor', e.currentTarget)}
            >
              <MenuIcon />
            </IconButton>
        
            <Menu
              anchorEl={menuState.mobileMenuAnchor}
              open={Boolean(menuState.mobileMenuAnchor)}
              onClose={handleMenuClose}
            >
              {navConfig.map((item) => (
                <div key={item.title}>
                  <MenuItem
                    sx={{ py: 0.5, my: 0 }}
                    autoFocus={false}
                    onClick={() => {
                      if (item.path) {
                        handleMenuClose();
                        window.location.href = item.path;
                      }
                    }}
                    disabled={item.disabled}
                  >
                    {item.title}
                  </MenuItem>                                    
                </div>
              ))}
            </Menu>
          </>
        )}        

        {/* ---------------------------------------------DESKTOP MENU START-------------------------------------------- */}          
        {!isMediumScreen && (
            <>
              <Box 
                sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  justifyContent: 'flex-start',
                  gap: 0.2,
                  ml: 7 // adjust value as needed (e.g., 1, 2, 3)
                }}
              >
                {/* Desktop Hamburger icon */}
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={(event) => updateMenuState('desktopMenuAnchor', event.currentTarget)}
                  sx={{ cursor: 'default' }}
                >
                  <MenuIcon />
                </IconButton>

                <Menu
                  anchorEl={menuState.desktopMenuAnchor}
                  open={Boolean(menuState.desktopMenuAnchor)}
                  onClose={handleMenuClose}
                >
                  {navConfig.map((item) => (
                    <div key={item.title}>                      
                      <MenuItem
                        autoFocus={false}
                        onClick={() => {
                          if (item.path) {
                            handleMenuClose();
                            window.location.href = item.path;
                          }
                        }}
                        disabled={item.disabled}
                      >
                        {item.title}
                      </MenuItem>                                                                
                    </div>
                  ))}
                </Menu>
              </Box>          

          {/* ---------------------------------------------ABOUT AND CONTACT START-------------------------------------------- */}            
            <Box sx={{ display: 'flex' }}>
              <Button
                color="inherit"
                component={Link}
                to="/about"
                sx={{
                  fontFamily: '"Raleway", sans-serif',
                  fontSize: {
                    xs: '0.7rem', // small font on extra small screens
                    sm: '0.85rem',
                    md: '0.9rem',
                    lg: '.98rem',   // large screens (desktops)
                    xl: '1.5rem',  // extra-large screens (large desktops, TVs)
                  },
                  textDecoration: isLinkActive(`/about`) ? 'underline' : 'none',
                  textUnderlineOffset: '6px',
                  textDecorationThickness: '2px',
                  color: isLinkActive(`/about`) ? '#FAEBD7' : '#FAEBD7', // caramel active, cream default
                  '&:hover': {
                    color: '#DAA520', // Golden amber; classic café warmth and brightness.
                  },
                }}
              >
                About
              </Button>

              <Button
                color="inherit"
                component={Link}
                to="/contact"
                sx={{
                  fontFamily: '"Raleway", sans-serif',
                  fontSize: {
                    xs: '0.7rem', // small font on extra small screens
                    sm: '0.85rem',
                    md: '0.9rem',
                    lg: '.98rem',   // large screens (desktops)
                    xl: '1.5rem',  // extra-large screens (large desktops, TVs)
                  },
                  ml: 5,
                  mr: 5,
                  textDecoration: isLinkActive(`/contact`) ? 'underline' : 'none',
                  textUnderlineOffset: '6px',
                  textDecorationThickness: '2px',
                  color: isLinkActive(`/contact`) ? '#FAEBD7' : '#FAEBD7',
                  '&:hover': {
                    color: '#DAA520', // Golden amber; classic café warmth and brightness.
                  },
                }}
              >
                Contact
              </Button>
            </Box>        
            </>
        )}
          
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default Navbar;